import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Tabs} from '@dropbox/dig-components/tabs';
import {Text, Title} from '@dropbox/dig-components/typography';
import {useQuery} from '@tanstack/react-query';
import {ROUTE_PATHS} from 'constant';
import {useUserPermissions} from 'hooks/use_user_permissions';
import {useState} from 'react';
import {Navigate} from 'react-router-dom';
import {API_URL, isSandboxMode} from 'utilities';

import styles from './admin.module.css';
import {MetricsTab} from './metrics_tab';

interface OptedOutUser {
  employee_id: string;
  name: string;
  email: string;
  location: string;
  opt_out_decisions: string[];
}

interface OptedOutUsersResponse {
  users: OptedOutUser[];
}

const useOptedOutUsers = () => {
  const isSandbox = isSandboxMode();
  return useQuery({
    queryKey: ['/admin/opted-out-users', isSandbox],
    queryFn: async (): Promise<OptedOutUsersResponse> => {
      const response = await fetch(`${API_URL}/api/v1/admin/opted-out-users`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch opted-out users');
      }
      return response.json();
    },
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 2,
  });
};

const formatOptOutDecision = (decision: string): string => {
  switch (decision) {
    case 'all':
      return 'All AI Features';
    case 'manager_assisted_feedback':
      return 'Manager Assisted Feedback';
    case 'stakeholder_feedback':
      return 'Stakeholder Feedback';
    default:
      return decision;
  }
};

export const AdminPanel = () => {
  const {isSuperuser, isLoading: permissionsLoading} = useUserPermissions();
  const [selectedTab, setSelectedTab] = useState('opt_outs');

  if (permissionsLoading) {
    return (
      <div className={styles['loading-container']}>
        <Spinner size="large" />
      </div>
    );
  }

  if (!isSuperuser) {
    return <Navigate to={ROUTE_PATHS.HOME} replace />;
  }

  return (
    <div className={styles['admin-container']}>
      <Tabs selectedTab={selectedTab} onSelection={setSelectedTab}>
        <Tabs.Group>
          <Tabs.Tab id="opt_outs">Opt Outs</Tabs.Tab>
          <Tabs.Tab id="metrics">Metrics</Tabs.Tab>
        </Tabs.Group>
        <Tabs.Panel tabId="opt_outs">
          <OptOutsTab />
        </Tabs.Panel>
        <Tabs.Panel tabId="metrics">
          <MetricsTab />
        </Tabs.Panel>
      </Tabs>
    </div>
  );
};

const OptOutsTab = () => {
  return (
    <div className={styles['metrics-container']}>
      <Title size="large" tagName="h1">
        SPRiTEsGPT: AI Opt Out Instructions
      </Title>
      <div className={styles['instructions']}>
        <Text variant="paragraph" tagName="p">
          This list contains employees who have opted out of AI usage for their
          performance reviews. As a result, the AI Feedback Summary, PDF
          generation in the ReviewGPT tab, and access to ReviewGPT from
          SPRiTEsGPT have been disabled for these employees&apos; managers.
        </Text>
        <Text variant="paragraph" tagName="p">
          To support privacy and legal compliance requirements, managers must be
          notified of these opt-outs so they can update any reviews in Workday
          that may have used AI-generated content.
        </Text>
        <Text variant="paragraph" tagName="p">
          Please follow the steps below to notify managers:
        </Text>
        <ol className={styles['instructions-list']}>
          <li>
            1. In Workday, navigate to the{' '}
            <strong>Performance Admin Hub</strong> and go to{' '}
            <strong>Employees &rarr; AI Opt Outs</strong>
          </li>
          <li>
            2. Compare the table on that page with the employee list below
          </li>
          <li>
            3. For any employee listed below who is not already included in
            Workday:
            <ol type="a" className={styles['instructions-sublist']}>
              <li>
                a. Click <strong>Add</strong>
              </li>
              <li>b. Select the employee&apos;s name</li>
              <li>c. Make any necessary updates to the default email</li>
              <li>
                d. Click the blue <strong>Add</strong> button
              </li>
            </ol>
          </li>
        </ol>
        <Text variant="paragraph" tagName="p">
          Once added, the employee will appear in the Workday list. The manager
          will receive an email notification from Workday along with an updated
          attestation requiring them to confirm that AI was not used to write
          the employee&apos;s review.
        </Text>
      </div>
      <OptedOutUsersTable />
    </div>
  );
};

const OptedOutUsersTable = () => {
  const {data, isLoading, isError} = useOptedOutUsers();

  if (isLoading) {
    return (
      <div className={styles['loading-container']}>
        <Spinner size="large" />
      </div>
    );
  }

  if (isError) {
    return (
      <Text variant="paragraph" tagName="p">
        Failed to load opted-out users.
      </Text>
    );
  }

  const users = data?.users ?? [];

  if (users.length === 0) {
    return (
      <div className={styles['table-container']}>
        <table className={styles['opted-out-table']}>
          <thead className={styles['sticky-header']}>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Location</th>
              <th>Opt-Out Decision(s)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan={4} className={styles['empty-message']}>
                There are no opted out users.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className={styles['table-container']}>
      <table className={styles['opted-out-table']}>
        <thead className={styles['sticky-header']}>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Location</th>
            <th>Opt-Out Decision(s)</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.employee_id}>
              <td>{user.name}</td>
              <td>{user.email}</td>
              <td>{user.location}</td>
              <td>
                {user.opt_out_decisions.map(formatOptOutDecision).join(', ')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
